import React from "react";

import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/src/lib/utils";
import { findFamilyByUuid } from "@/src/utils/fontFaces";

import {
  FONTS_SECTION_ID,
  OPENABLE_SECTION_ID_LIST,
  SECTION_GROUP_LIST,
  SECTION_LABEL_KEY_BY_ID,
} from "./sections";

import type { JSX } from "react";

import type { FontFamily, ProjectFontRoles } from "@/src/api/StylesAPI.types";

import type { SectionId } from "./sections";

interface SectionListProps {
  familyList: FontFamily[];
  isFontsLoading: boolean;
  roles: ProjectFontRoles;
  onOpenSection: (sectionId: SectionId) => void;
}

function getFontsSummary(
  familyList: FontFamily[],
  roles: ProjectFontRoles,
  isFontsLoading: boolean,
  t: (key: string) => string,
): string {
  if (isFontsLoading) return t("loadingFonts");

  const titleFamilyName =
    findFamilyByUuid(familyList, roles.title)?.display_name ??
    t("unsetFontRole");
  const textFamilyName =
    findFamilyByUuid(familyList, roles.text)?.display_name ??
    t("unsetFontRole");

  return `${titleFamilyName} / ${textFamilyName}`;
}

function SectionList({
  familyList,
  isFontsLoading,
  roles,
  onOpenSection,
}: SectionListProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");

  const summaryBySectionId: Partial<Record<SectionId, string>> = {
    [FONTS_SECTION_ID]: getFontsSummary(familyList, roles, isFontsLoading, t),
  };

  return (
    <div className="flex flex-col pb-8">
      <div className="px-[18px] pt-[18px] pb-2.5">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
          {t("sectionsIntroEyebrow")}
        </p>
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
          {t("sectionsIntroBody")}
        </p>
      </div>
      {SECTION_GROUP_LIST.map((group) => (
        <React.Fragment key={group.titleKey}>
          <p className="text-muted-foreground px-[18px] pt-[22px] pb-1.5 text-[11px] font-semibold tracking-[0.06em] uppercase">
            {t(group.titleKey)}
          </p>
          {group.sectionIdList.map((sectionId) => {
            const isEnabled = OPENABLE_SECTION_ID_LIST.includes(sectionId);

            return (
              <button
                key={sectionId}
                data-testid={`section-row-${sectionId}`}
                disabled={!isEnabled}
                onClick={(): void => onOpenSection(sectionId)}
                type="button"
                className={cn(
                  "border-border/70 flex w-full items-center gap-3 border-t px-[18px] py-[13px] text-left",
                  isEnabled
                    ? "hover:bg-muted cursor-pointer"
                    : "cursor-not-allowed opacity-50",
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="text-foreground block text-[13.5px] font-medium">
                    {t(SECTION_LABEL_KEY_BY_ID[sectionId])}
                  </span>
                  <span
                    className="text-muted-foreground mt-0.5 block truncate text-xs"
                    data-testid={`section-summary-${sectionId}`}
                  >
                    {summaryBySectionId[sectionId] ?? t("sectionComingSoon")}
                  </span>
                </span>
                <ChevronRight className="text-muted-foreground size-4 shrink-0" />
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

export default SectionList;
